import {
  MutationCache,
  QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { meQueryKey } from '@/api/queryKeys';
import { getApiErrorMessage } from '@/utils/apiError';

import { toaster } from './toaster';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: CustomMutationMeta;
  }
}

interface CustomMutationMeta {
  skipGlobalErrorHandler?: boolean;
}

export type MutationConfig<
  MutationFnType extends (...args: any[]) => Promise<unknown>,
> = UseMutationOptions<
  Awaited<ReturnType<MutationFnType>>,
  Error,
  Parameters<MutationFnType>[0]
>;

/**
 * A 4xx answers the same on every try (an unknown email stays unknown), so only
 * network failures and 5xx are retried, with React Query's default count.
 */
export const shouldRetry = (failureCount: number, error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return failureCount < 3;
};

export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // The gates read a cached /v1/users/me. A 403 says the cache and the
      // server disagree, so drop it rather than keep gating on it (#419 AC 13).
      // Ahead of the opt-out: suppressing a toast must not also suppress fixing
      // the cache.
      if (isAxiosError(error) && error.response?.status === 403) {
        void queryClient.invalidateQueries({
          queryKey: meQueryKey.all,
        });
      }

      if (mutation.meta?.skipGlobalErrorHandler) {
        return;
      }
      toaster.error(getApiErrorMessage(error, 'An unexpected error occurred'));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 2 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
  },
});
